// Shared validation functions for ResumeTab components

// Helper function to validate month value
export const isValidMonth = (month) => {
    if (!month) return true; // Empty is valid (optional field)
    const monthNum = parseInt(month, 10);
    return monthNum >= 1 && monthNum <= 12;
};

// Helper function to validate year value
export const isValidYear = (year) => {
    if (!year) return true; // Empty is valid (optional field)
    const yearNum = parseInt(year, 10);
    const currentYear = new Date().getFullYear();
    return yearNum >= (currentYear - 70) && yearNum <= (currentYear + 10);
};

// Helper function to validate day value
export const isValidDay = (day) => {
    if (!day) return true; // Empty is valid (optional field)
    const dayNum = parseInt(day, 10);
    return dayNum >= 1 && dayNum <= 31;
};

// Validate personal info
export const validatePersonalInfo = (formData) => {
    const errors = {};
    if (!formData.first_name?.trim()) {
        errors.first_name = 'First name is required';
    }
    if (!formData.last_name?.trim()) {
        errors.last_name = 'Last name is required';
    }
    if (!formData.mobile_number?.trim()) {
        errors.mobile_number = 'Phone number is required';
    }
    // Validate DOB components
    if (!formData.dob_day || !formData.dob_month || !formData.dob_year) {
        errors.date_of_birth = 'Date of birth is required';
    }
    if (!formData.address_line_1?.trim()) {
        errors.address_line_1 = 'Address line 1 is required';
    }
    if (!formData.city?.trim()) {
        errors.city = 'City is required';
    }
    if (!formData.state?.trim()) {
        errors.state = 'State/Province is required';
    }
    if (!formData.country?.trim()) {
        errors.country = 'Country is required';
    }
    // Validate profile picture
    if (!formData.profile_picture_url) {
        errors.profile_picture = 'Profile picture is required';
    }
    return errors;
};

// Get list of missing mandatory personal info fields
export const getMissingPersonalFields = (formData) => {
    const missing = [];
    if (!formData.first_name?.trim()) missing.push('First Name');
    if (!formData.last_name?.trim()) missing.push('Last Name');
    if (!formData.mobile_number?.trim()) missing.push('Phone Number');
    if (!formData.dob_day || !formData.dob_month || !formData.dob_year) missing.push('Date of Birth');
    if (!formData.address_line_1?.trim()) missing.push('Address Line 1');
    if (!formData.city?.trim()) missing.push('City');
    if (!formData.state?.trim()) missing.push('State/Province');
    if (!formData.country?.trim()) missing.push('Country');
    if (!formData.profile_picture_url) missing.push('Profile Picture');
    return missing;
};

// Validate education entries
export const validateEducation = (educationList) => {
    const errors = [];
    educationList.forEach((edu, index) => {
        const entryErrors = {};
        if (!edu.school?.trim() && !edu.institution?.trim()) {
            entryErrors.school = 'School is required';
        }
        if (!edu.degree?.trim()) {
            entryErrors.degree = 'Degree is required';
        }
        if (!edu.start_month || !edu.start_year) {
            entryErrors.start_date = 'Start Date is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

// Validate experience entries
export const validateExperience = (experienceList) => {
    const errors = [];
    experienceList.forEach((exp, index) => {
        const entryErrors = {};
        if (!exp.company?.trim()) {
            entryErrors.company = 'Company is required';
        }
        if (!exp.role?.trim() && !exp.title?.trim()) {
            entryErrors.role = 'Role is required';
        }
        if (!exp.start_month || !exp.start_year) {
            entryErrors.start_date = 'Start Date is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

// Validate project entries
export const validateProjects = (projectsList) => {
    const errors = [];
    projectsList.forEach((proj, index) => {
        const entryErrors = {};
        if (!proj.name?.trim()) {
            entryErrors.name = 'Project name is required';
        }
        // Projects dates are optional, no validation needed for month/year fields
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

// Validate publication entries
export const validatePublications = (publicationsList) => {
    const errors = [];
    publicationsList.forEach((pub, index) => {
        const entryErrors = {};
        if (!pub.title?.trim()) {
            entryErrors.title = 'Title is required';
        }
        // Publication dates are optional month/year fields
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

// Validate certification entries
export const validateCertifications = (certificationsList) => {
    const errors = [];
    certificationsList.forEach((cert, index) => {
        const entryErrors = {};
        if (!cert.name?.trim()) {
            entryErrors.name = 'Certification name is required';
        }
        // Certification dates are optional month/year fields
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

// Validate award entries
export const validateAwards = (awardsList) => {
    const errors = [];
    awardsList.forEach((award, index) => {
        const entryErrors = {};
        if (!award.name?.trim()) {
            entryErrors.name = 'Award name is required';
        }
        // Award dates are optional month/year fields
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

// Format error messages for display
export const formatValidationErrors = (errors, sectionName) => {
    if (!errors || errors.length === 0) return '';
    
    const messages = errors.map(err => {
        const fields = Object.keys(err).filter(k => k !== 'index');
        return `Entry ${err.index + 1}: ${fields.map(f => err[f]).join(', ')}`;
    });
    
    return `${sectionName}: ${messages.join('; ')}`;
};
